import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  downloadBannerAnalytics,
  getBannerAnalytics,
  getBannerExportStatus,
} from 'app/api/banner';
import { startExportPoll, triggerDownload } from 'app/utils/exportPoller';
import Message from 'app/utils/Message';
import Toast from 'app/utils/Toast';

const DATA_PER_PAGE = 10;
const DEFAULT_SORT_BY = 'lastOpened';
const DEFAULT_SORT_ORDER = 'desc';

// Mirrors the backend's BannerAnalyticsService.formatDurationMs exactly
// (D-07's "Total time" cell) — hours unpadded/unbounded, minutes/seconds
// always two digits.
export function formatDurationMs(ms) {
  const totalSeconds = Math.floor(Math.max(0, ms || 0) / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return `${hours}:${pad(minutes)}:${pad(seconds)}`;
}

// Banner Analytics page hook (BL-11/BL-12; D-01, D-02, D-04..D-08, D-18).
// Calls only the three app/api/banner functions — it loads no mall or tier
// option lists (D-04/D-05: no mall or tier column/filter anywhere here).
const useBannerAnalytics = () => {
  const { id: bannerId } = useParams();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY);
  const [sortOrder, setSortOrder] = useState(DEFAULT_SORT_ORDER);

  // Draft vs applied date range (D-06, the only filter): draft holds the
  // in-progress inputs, applied holds what the last successful Apply/Reset
  // actually sent — only the applied pair drives the fetch effect below.
  const [draftStartDate, setDraftStartDate] = useState('');
  const [draftEndDate, setDraftEndDate] = useState('');
  const [appliedStartDate, setAppliedStartDate] = useState('');
  const [appliedEndDate, setAppliedEndDate] = useState('');
  const [dateError, setDateError] = useState('');

  const downloadInProgressRef = useRef(false);
  const [downloadInProgress, setDownloadInProgress] = useState(false);

  // Single fetch path. Every state change that should trigger a refetch
  // (pagination, sort, applied date range) goes through the deps below, so
  // exactly one request fires per commit, no matter how many of these are
  // batched together in a single handler (onSort/handleApply/handleReset).
  const fetchAnalytics = useCallback(async () => {
    if (!bannerId) return;
    setLoading(true);
    try {
      const params = {
        bannerId,
        page,
        limit: DATA_PER_PAGE,
        sortBy,
        sortOrder,
        startDate: appliedStartDate || undefined,
        endDate: appliedEndDate || undefined,
      };
      const response = await getBannerAnalytics(params);
      if (response?.responseCode === 200) {
        setSummary(response?.data?.banner || null);
        setRows(response?.data?.rows || []);
        setCount(response?.data?.count || 0);
      } else {
        // 400/404 business-rule failures (D-03) are Toasted, never a
        // logout — the admin FE only logs out on 401/403, and this route
        // never returns those for a business-rule failure.
        Toast.error(
          response?.response?.message || Message.Error.COMMON_MESSAGE
        );
      }
    } catch (error) {
      Toast.error(Message.Error.COMMON_MESSAGE);
    } finally {
      setLoading(false);
    }
  }, [bannerId, page, sortBy, sortOrder, appliedStartDate, appliedEndDate]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Wired as PaginatedTable's `fetchData` prop. It auto-calls this on mount
  // and whenever its own internal currentPage changes (Next/Prev/page-
  // number clicks). Setting `page` to the value it already holds is a
  // no-op in React (no re-render), so this never double-fetches against
  // the mount-time effect above.
  const fetchData = useCallback((newPage) => {
    setPage(newPage || 1);
  }, []);

  // Column header click (D-08): same column toggles asc<->desc; a
  // different column becomes active at asc. Always resets to page 1.
  const onSort = useCallback(
    (columnId) => {
      if (sortBy === columnId) {
        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(columnId);
        setSortOrder('asc');
      }
      setPage(1);
    },
    [sortBy]
  );

  const handleApply = useCallback(() => {
    if (draftStartDate && draftEndDate && draftStartDate > draftEndDate) {
      setDateError('Start date must not be after end date');
      return;
    }
    setDateError('');
    setPage(1);
    setAppliedStartDate(draftStartDate);
    setAppliedEndDate(draftEndDate);
  }, [draftStartDate, draftEndDate]);

  const handleReset = useCallback(() => {
    setDraftStartDate('');
    setDraftEndDate('');
    setDateError('');
    setPage(1);
    setAppliedStartDate('');
    setAppliedEndDate('');
  }, []);

  // Filter side panel (shared FilterModel, same as Voucher Management and
  // the reports): Submit applies and closes, an invalid range keeps it open
  // with the error; Cancel closes and drops unsubmitted edits.
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const handleFilterSubmit = useCallback(
    (event) => {
      event?.preventDefault?.();
      if (draftStartDate && draftEndDate && draftStartDate > draftEndDate) {
        setDateError('Start date must not be after end date');
        return;
      }
      handleApply();
      setIsFilterOpen(false);
    },
    [draftStartDate, draftEndDate, handleApply]
  );

  const handleFilterCancel = useCallback(() => {
    setDraftStartDate(appliedStartDate);
    setDraftEndDate(appliedEndDate);
    setDateError('');
    setIsFilterOpen(false);
  }, [appliedStartDate, appliedEndDate]);

  // Async export (BL-06/BL-07): returns a jobId, then polls the
  // banner-scoped export-status route (D-02) — never the shared
  // /admin/export/status.
  const handleDownload = useCallback(async () => {
    if (downloadInProgressRef.current) return;
    downloadInProgressRef.current = true;
    setDownloadInProgress(true);

    const finish = () => {
      downloadInProgressRef.current = false;
      setDownloadInProgress(false);
    };

    try {
      const response = await downloadBannerAnalytics({
        bannerId,
        sortBy,
        sortOrder,
        startDate: appliedStartDate || undefined,
        endDate: appliedEndDate || undefined,
      });
      const jobId = response?.data?.jobId;
      if (response?.responseCode !== 200 || !jobId) {
        Toast.error(
          response?.response?.message || Message.Error.COMMON_MESSAGE
        );
        finish();
        return;
      }

      Toast.success('Export started');

      startExportPoll(jobId, {
        getStatus: getBannerExportStatus,
        onDone: (url) => {
          triggerDownload(url);
          finish();
        },
        onFailed: (error) => {
          Toast.error(error || Message.Error.COMMON_MESSAGE);
          finish();
        },
        onError: () => {
          Toast.error(Message.Error.COMMON_MESSAGE);
          finish();
        },
      });
    } catch (error) {
      Toast.error(Message.Error.COMMON_MESSAGE);
      finish();
    }
  }, [bannerId, sortBy, sortOrder, appliedStartDate, appliedEndDate]);

  return {
    bannerId,
    navigate,
    rows,
    count,
    summary,
    loading,
    page,
    dataPerPage: DATA_PER_PAGE,
    sortBy,
    sortOrder,
    onSort,
    fetchData,
    draftStartDate,
    draftEndDate,
    setDraftStartDate,
    setDraftEndDate,
    appliedStartDate,
    appliedEndDate,
    dateError,
    handleApply,
    handleReset,
    isFilterOpen,
    setIsFilterOpen,
    handleFilterSubmit,
    handleFilterCancel,
    downloadInProgress,
    handleDownload,
  };
};

export default useBannerAnalytics;
